import "reflect-metadata";

import {
  IsUUID,
  IsString,
  IsInt,
  Min,
  Max,
  IsEnum,
  IsOptional,
  IsDateString
} from "class-validator";
import { Type } from "class-transformer";

import SubscriptionUnit from "./unit";
import ProgramInspector from "@/models/program/inspector";
import { EInspectorStatus } from "@/enums/inspector_status";
import { PriorityTypes } from "@/enums/priority_types";
import { ESubscriptionProgressStatus, ESubscriptionStatus } from "./status";

export interface ISubscriptionInspector {
  readonly id: string;
  readonly comment: string;
  readonly level: number;
  readonly status: EInspectorStatus;
  readonly updated_at: string;
  readonly inspector_name: string;
  readonly inspector_files: Array<{ id: string; url: string }>;
}

export interface ISubscriptionComment {
  readonly id: string;
  readonly text: string;
  readonly created_at: string;
  readonly comment_files: Array<{ id: string; url: string }>;
}

export interface ISubscriptionUser {
  readonly id: string;
  readonly full_name: string;
}

export default class SubscriptionProgram {
  @IsUUID("4")
  public readonly id!: string;

  @IsUUID("4")
  public readonly program_version_id!: string;

  @IsUUID("4")
  public readonly user_id!: string;

  @IsUUID("4")
  public readonly mentor_id!: string;

  @IsString()
  public readonly program_version_title!: string;

  @IsString()
  public readonly program_version_description!: string;

  @IsString()
  public readonly pillar_full_name!: string;

  @IsString()
  public readonly pillar_short_name!: string;

  @IsOptional()
  @IsString()
  public readonly pillar_color?: string;

  @IsString()
  public readonly skill_full_name!: string;

  @IsString()
  public readonly workplace_name!: string;

  @IsUUID("4")
  public readonly skill_id!: string;

  @IsOptional()
  @IsUUID("4")
  public readonly program_assignment_id?: string;

  @IsOptional()
  @IsUUID("4")
  public readonly assignment_user_workplace_id?: string;

  @IsInt()
  @Min(0)
  @Max(4)
  public readonly skill_level!: string;

  @IsEnum(ESubscriptionStatus)
  public readonly status!: ESubscriptionStatus;

  @IsEnum(ESubscriptionProgressStatus)
  public readonly progress_status!: ESubscriptionProgressStatus;

  @IsInt()
  @Min(0)
  @Max(4)
  public readonly current_skill_level!: number;

  @IsInt()
  public readonly confirmation_levels: number[] = [];

  public readonly current_comment?: ISubscriptionComment;

  @IsDateString()
  public readonly starts_at!: string;

  @IsDateString()
  public readonly deadline_at!: string;

  @Type(() => SubscriptionUnit)
  public subscription_units!: SubscriptionUnit[];

  @Type(() => ProgramInspector)
  public subscription_inspectors!: ProgramInspector[];

  public failed_inspector?: ISubscriptionInspector | null;
  public user!: ISubscriptionUser;

  public get awaitingContinue() {
    return this.status === ESubscriptionStatus.AWAITING_CONTINUE;
  }

  public get awaitingConfirmation() {
    return this.status === ESubscriptionStatus.AWAITING_CONFIRMATION;
  }

  public get failed() {
    return this.status === ESubscriptionStatus.FAILED;
  }

  public get awaitingCompletion() {
    return this.status === ESubscriptionStatus.AWAITING_COMPLETION;
  }

  public get awaitingNext() {
    return this.awaitingCompletion || this.awaitingContinue;
  }
}

export interface IStudentProgramsData {
  readonly id: string;
  readonly color: string;
  readonly progress_status: ESubscriptionProgressStatus;
  readonly program_status: ESubscriptionStatus;
  readonly program_percent: number;
  readonly starts_at: string;
  readonly program_id: string;
  readonly program_version_id: string;
  readonly deadline_at: string;
  readonly is_deadline_missed: boolean;
  readonly is_skill_assignment: boolean;
  readonly program_version_title: string;
  readonly days_to_study: number;
  readonly skill_id: string;
  readonly skill_name: string;
  readonly pillar_id?: string;
  readonly pillar_name?: string;
  readonly pillar_short_name?: string;
  readonly pillar_color?: string;

  readonly in_horizon?: boolean;
  readonly program_assignment_id?: string;
  readonly assignment_user_workplace_id?: string;

  readonly priority_type?: PriorityTypes;
  readonly priority?: number | null;
}

export interface IDataPrograms {
  readonly current_page: number;
  readonly next_page: boolean;
  readonly programs: IStudentProgramsData[];
}
